import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
  palette: {
    primary: {
      main: '#0077B8', // Logo core blue
      light: '#4DB9FF',
      dark: '#004F82',
      darker: '#003955',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#F5A000', // Accent yellow from logo
      light: '#FFC94D',
      dark: '#C87700',
      contrastText: '#000000',
    },
    accent: {
      main: '#00ACEA', // Bright cyan highlight
      light: '#40C9FF',
      dark: '#0078A8',
    },
    success: {
      main: '#10b981', // Emerald Green
      light: '#34d399',
      dark: '#059669',
    },
    warning: {
      main: '#f59e0b', // Amber - couleur d'accent chaleureuse
      light: '#fbbf24',
      dark: '#d97706',
      contrastText: '#000000',
    },
    error: {
      main: '#ef4444',
      light: '#f87171',
      dark: '#dc2626',
    },
    background: {
      default: '#f0fdfa', // Cyan très clair pour le fond
      paper: '#ffffff',
      secondary: '#ccfbf1',
    },
    text: {
      primary: '#134e4a', // Teal foncé pour un excellent contraste
      secondary: '#475569',
      disabled: '#94a3b8',
    },
    divider: 'rgba(0, 119, 184, 0.18)',
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: 16,
    h1: {
      fontSize: '3.5rem',
      fontWeight: 900,
      letterSpacing: '-0.05em',
      lineHeight: 1.1,
      '@media (max-width:960px)': {
        fontSize: '2.75rem',
      },
      '@media (max-width:600px)': {
        fontSize: '2rem',
      },
    },
    h2: {
      fontSize: '2.75rem',
      fontWeight: 800,
      letterSpacing: '-0.04em',
      lineHeight: 1.2,
      '@media (max-width:960px)': {
        fontSize: '2.25rem',
      },
      '@media (max-width:600px)': {
        fontSize: '1.75rem',
      },
    },
    h3: {
      fontSize: '2.25rem',
      fontWeight: 700,
      letterSpacing: '-0.03em',
      lineHeight: 1.3,
      '@media (max-width:960px)': {
        fontSize: '1.875rem',
      },
      '@media (max-width:600px)': {
        fontSize: '1.5rem',
      },
    },
    h4: {
      fontSize: '1.875rem',
      fontWeight: 700,
      letterSpacing: '-0.02em',
      '@media (max-width:600px)': {
        fontSize: '1.375rem',
      },
    },
    h5: {
      fontSize: '1.5rem',
      fontWeight: 600,
      '@media (max-width:600px)': {
        fontSize: '1.25rem',
      },
    },
    h6: {
      fontSize: '1.25rem',
      fontWeight: 600,
      '@media (max-width:600px)': {
        fontSize: '1.125rem',
      },
    },
    button: {
      fontWeight: 700,
      letterSpacing: '0.025em',
      textTransform: 'none',
      '@media (max-width:600px)': {
        fontSize: '0.938rem',
      },
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.7,
      '@media (max-width:600px)': {
        fontSize: '0.938rem',
      },
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
      '@media (max-width:600px)': {
        fontSize: '0.813rem',
      },
    },
  },
  shape: {
    borderRadius: 16,
  },
  shadows: [
    'none',
    '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
    '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
    '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
  ],
  components: {
    MuiContainer: {
      defaultProps: {
        maxWidth: 'xl',
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding: '10px 24px',
          fontSize: '1rem',
          fontWeight: 700,
          boxShadow: 'none',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '@media (max-width:600px)': {
            padding: '8px 20px',
            fontSize: '0.938rem',
          },
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 12px 24px -10px rgba(14, 165, 233, 0.4)',
          },
        },
        sizeLarge: {
          padding: '12px 32px',
          '@media (max-width:600px)': {
            padding: '10px 24px',
          },
        },
        contained: {
          background: 'linear-gradient(135deg, #0077B8 0%, #00ACEA 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #00639A 0%, #008FC5 100%)',
            boxShadow: '0 16px 32px -12px rgba(0, 119, 184, 0.5)',
          },
        },
        outlined: {
          borderWidth: '2px',
          borderColor: 'primary.main',
          '&:hover': {
            borderWidth: '2px',
            borderColor: 'primary.dark',
            bgcolor: 'rgba(14, 165, 233, 0.04)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          border: '1px solid',
          borderColor: '#e0f2fe',
          background: 'linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%)',
          boxShadow: '0 4px 16px -4px rgba(14, 165, 233, 0.1)',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          '@media (max-width:600px)': {
            borderRadius: 16,
          },
          '&:hover': {
            transform: 'translateY(-8px) scale(1.01)',
            boxShadow: '0 24px 48px -12px rgba(14, 165, 233, 0.25)',
            borderColor: '#bae6fd',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 700,
          borderRadius: 10,
          fontSize: '0.875rem',
          '@media (max-width:600px)': {
            fontSize: '0.813rem',
            height: '26px',
          },
        },
        filled: {
          background: 'linear-gradient(135deg, #0077B8 0%, #00ACEA 100%)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        elevation1: {
          boxShadow: '0 2px 8px 0 rgba(14, 165, 233, 0.08)',
        },
        elevation2: {
          boxShadow: '0 4px 16px 0 rgba(14, 165, 233, 0.12)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(14, 165, 233, 0.15)',
            },
            '&.Mui-focused': {
              boxShadow: '0 6px 16px rgba(14, 165, 233, 0.2)',
            },
          },
        },
      },
    },
  },
});

export default theme;
